import { GetItemsResponse } from "@amzn/amazonian-hardware-catalogical";
import { FilterSelection } from "@amzn/stencil-react-components/filtering";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { sendSiteSearchMetric } from "common/helpers/analytics-helper";
import {
  buildTopNResults,
  EventTrigger,
  generateQueryId,
  SearchResultsShownPayload,
  trackSearchResultsError,
  trackSearchResultsShown,
} from "common/helpers/search-analytics";
import { CatalogURLQueryStrings } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { ItemModel } from "lib/services/catalogical-models";
import {
  CatalogicalSearchRequestContent,
  SearchExperienceDomains,
  SearchExperienceService,
} from "lib/services/shop-search-service";
import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react";
import { CostOptions, SearchFilter } from "view/Page/SearchResultsV2/SearchFilterPanel";

export interface UseSearchExperienceHookResult<T> {
  isLoading: boolean;
  isLoadingMore: boolean;
  searchCatalogItems: (
    query: CatalogicalSearchRequestContent,
    shouldSendMetric?: boolean,
    telemetry?: SearchEventMetadata
  ) => Promise<GetItemsResponse<T> | undefined>;
  searchNext: (query: CatalogicalSearchRequestContent) => Promise<GetItemsResponse<T> | undefined>;
  items: T[];
  totalCount: number;
  nextToken: number;
  hasMoreItems: boolean;
  queryId: string | null;
  actions: {
    onNameSortChange: (e: any) => void;
    onPriceSortChange: (e: any) => void;
    onBestMatchSortChange: (e: any) => void;
    onRatingsSortChange: (e: any) => void;
    onFilterChange: (e: any) => void;
    transformToCatalogicalFilters: (e: any) => Record<string, any>;
  };
  currentSort: SortProps;
  nameSort: SortProps;
  priceSort: SortProps;
  bestMatchSort: SortProps;
  ratingsSort: SortProps;
  filter: FilterProps;
  initializeFromUrl: (params: UrlInitParams) => void;
  shouldUseBestMatch: (searchQuery: string, hasActiveSort: boolean) => boolean;
}

export interface SortProps {
  value?: FilterSelection;
  sortBy?: string;
  sortDirection?: string;
}

export interface FilterProps {
  os?: string[];
  category?: string[];
  costCondition?: string[];
}

export interface UrlInitParams {
  sort?: string;
  filters?: FilterProps;
}

export interface SearchEventMetadata {
  triggerSearchboxQueryId?: string | null;
  eventTrigger?: EventTrigger;
}

/**
 * Hook for searching catalog items using the SearchExperienceService
 * @template T The type of items returned by the search
 * @param {SearchExperienceDomains} domain - The domain to search in
 * @returns {UseSearchExperienceHookResult<T>} Object containing search functions and state
 */
export function useSearchExperience<T extends ItemModel>(
  domain: SearchExperienceDomains = "software"
): UseSearchExperienceHookResult<T> {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMoreItems, setHasMoreItems] = useState<boolean>(false);
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [nextToken, setNextToken] = useState<number>(0);

  // QueryId management for telemetry
  const queryId: MutableRefObject<string | null> = useRef(null);

  const [currentSort, setCurrentSort] = useState<SortProps>({});
  const [nameSort, setNameSort] = useState<SortProps>({});
  const [priceSort, setPriceSort] = useState<SortProps>({});
  const [bestMatchSort, setBestMatchSort] = useState<SortProps>({});
  const [ratingsSort, setRatingsSort] = useState<SortProps>({});
  const [filter, setFilter] = useState<FilterProps>({});

  const clearSort = useCallback(() => {
    setNameSort({});
    setPriceSort({});
    setBestMatchSort({});
    setRatingsSort({});
    setCurrentSort({});
  }, []);

  const onNameSortChange = useCallback(
    (value: string) => {
      clearSort();

      if (value) {
        const [sortBy, sortDirection] = value.split(",");
        setNameSort({ value, sortBy, sortDirection });
        setCurrentSort({ value, sortBy, sortDirection });
      }
    },
    [clearSort]
  );

  const onPriceSortChange = useCallback(
    (value: string) => {
      clearSort();

      if (value) {
        const [sortBy, sortDirection] = value.split(",");
        setPriceSort({ value, sortBy, sortDirection });
        setCurrentSort({ value, sortBy, sortDirection });
      }
    },
    [clearSort]
  );

  const onBestMatchSortChange = useCallback(
    (value: string) => {
      clearSort();

      if (value) {
        setBestMatchSort({ value });
        setCurrentSort({ value });
      }
    },
    [clearSort]
  );

  const onRatingsSortChange = useCallback(
    (value: string) => {
      clearSort();

      if (value) {
        const [uiSortBy, sortDirection] = value.split(",");
        // Keep the backend field mapping in ratingsSort only; currentSort stores
        // the UI-friendly value so that other handlers (e.g. handleFilterChange)
        // persist a clean URL when they read currentSort.sortBy.
        const backendSortBy = "feedback.averageRating,feedback.numberOfRatings";
        setRatingsSort({ value, sortBy: backendSortBy, sortDirection });
        setCurrentSort({ value, sortBy: uiSortBy, sortDirection });
      }
    },
    [clearSort]
  );

  /**
   * Maps cost filter options to ITPT classification values for catalog filtering
   * @param values - Array of selected cost options (Free, ChargeToCostCenter)
   * @returns Array of ITPT classifications or undefined if no mappings found
   */
  const getItptConfig = (values: string[]) => {
    const classifications: string[] = [];
    const itptMapping = {
      [CostOptions.Free]: [], // Free items have no specific classification
      [CostOptions.ChargeToCostCenter]: ["Discretionary", "-"], // These items use these classifications
    };
    values?.forEach((value) => {
      classifications.push(...itptMapping[value as CostOptions]);
    });
    return classifications.length > 0 ? classifications : undefined;
  };

  /**
   * Maps cost filter options to price amount filters for catalog search
   * @param values - Array of selected cost options (Free, ChargeToCostCenter)
   * @returns Array of price amounts to filter by or undefined if no price filtering needed
   */
  const getItptPriceFilter = (values: string[]): number[] | undefined => {
    const prices: number[] = [];
    const itptMapping = {
      [CostOptions.Free]: [0], // Free items have price of 0
      [CostOptions.ChargeToCostCenter]: [], // No specific price filter - filtering by itpt classification config instead
    };

    values?.forEach((value) => {
      prices.push(...itptMapping[value as CostOptions]);
    });

    return prices.length > 0 ? prices : undefined;
  };

  const onFilterChange = useCallback((filters: any) => {
    setFilter({
      [SearchFilter.OS]: filters[SearchFilter.OS],
      [SearchFilter.CATEGORY]: filters[SearchFilter.CATEGORY],
      [SearchFilter.COST_CONDITION]: filters[SearchFilter.COST_CONDITION],
    });
  }, []);

  const transformToCatalogicalFilters = (filters: any): Record<string, any> => {
    return {
      os: filters[SearchFilter.OS],
      categories: filters[SearchFilter.CATEGORY],
      itptClassification: getItptConfig(filters[SearchFilter.COST_CONDITION]),
      "price.amount": getItptPriceFilter(filters[SearchFilter.COST_CONDITION]),
    };
  };

  // Initialize state from URL parameters
  const initializeFromUrl = useCallback((params: UrlInitParams) => {
    if (params.sort) {
      const [sortBy, sortDirection] = params.sort.split(",");

      if (sortBy === CatalogURLQueryStrings.NAME) {
        setNameSort({ value: params.sort, sortBy, sortDirection });
        setCurrentSort({ value: params.sort, sortBy, sortDirection });
      } else if (sortBy === CatalogURLQueryStrings.AMOUNT) {
        setPriceSort({ value: params.sort, sortBy, sortDirection });
        setCurrentSort({ value: params.sort, sortBy, sortDirection });
      }
    }

    if (params.filters) {
      setFilter(params.filters);
    }
  }, []);

  // Function to determine if Best Match should be active
  const shouldUseBestMatch = useCallback((searchQuery: string, hasActiveSort: boolean) => {
    // Best Match is active only when:
    // 1. There is a search query (not just browsing)
    // 2. No other sorting is explicitly applied
    return searchQuery.trim() !== "" && !hasActiveSort;
  }, []);

  const { addErrorBanner } = useMessageBannersBox();
  const searchService = useMemo(() => new SearchExperienceService(domain), [domain]);

  const trackSearchResultsToUxm = (
    searchText: string | undefined,
    result: GetItemsResponse<T>,
    resultCount: number,
    nextToken: number,
    apiRequest: CatalogicalSearchRequestContent,
    metadata?: SearchEventMetadata
  ) => {
    const offset = result.pagination.offset;
    const topItems = buildTopNResults(result.data, offset, domain);

    const payload: SearchResultsShownPayload = {
      queryId: queryId.current ?? "-",
      searchText: searchText ?? "",
      nextToken: nextToken,
      filters: { ...filter },
      sort: currentSort,
      visibleResultsCount: result.data.length,
      resultCount: resultCount,
      topNResults: topItems,
      domain: domain,
      request: apiRequest,
      ...metadata,
    };
    trackSearchResultsShown(payload);
  };

  /**
   * Search for catalog items with the given query parameters
   * @param query - The search query parameters
   * @param shouldSendMetric - Whether to send search metrics
   * @returns The search response or undefined if an error occurred
   */
  const searchCatalogItems = async (
    query: CatalogicalSearchRequestContent,
    shouldSendMetric: boolean = false,
    metadata?: SearchEventMetadata
  ): Promise<GetItemsResponse<T> | undefined> => {
    setIsLoading(true);

    // Generate new queryId for this search
    const newQueryId = generateQueryId();
    queryId.current = newQueryId;

    try {
      const result = await searchService.getItems<T>(query);

      const hasMoreItems = !(result.pagination.offset + result.pagination.limit >= result.pagination.total);
      setHasMoreItems(hasMoreItems);

      // Update state with search results
      const data = result.data;
      const resultCount = result.pagination.total;
      const newNextToken = result.pagination.offset + result.pagination.limit;

      setItems(data);
      setTotalCount(resultCount);
      setNextToken(newNextToken);

      // Telemetry
      trackSearchResultsToUxm(query.searchQuery, result, resultCount, newNextToken, query, metadata);

      // Send metrics if requested
      if (shouldSendMetric && query.searchQuery) {
        sendSiteSearchMetric({
          totalResults: result.pagination.total,
          terms: query.searchQuery.split(" "),
        });
      }

      return result;
    } catch (error) {
      trackSearchResultsError({
        queryId: queryId.current,
        error: (error as Error).message,
      });
      addErrorBanner(getString("errors.general"));
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load the next page of search results
   * Preserves the same queryId as the initial search
   * @param query - The search query parameters
   * @returns The search response or undefined if an error occurred
   */
  const searchNext = async (query: CatalogicalSearchRequestContent): Promise<GetItemsResponse<T> | undefined> => {
    setIsLoadingMore(true);

    try {
      const result = await searchService.getItems<T>(query);

      const newNextToken = result.pagination.offset + result.pagination.limit;

      // Append new items to existing items
      setItems((prevItems) => [...prevItems, ...result.data]);
      setNextToken(newNextToken);

      const hasMoreItems = !(result.pagination.offset + result.pagination.limit >= result.pagination.total);
      setHasMoreItems(hasMoreItems);

      // Telemetry
      trackSearchResultsToUxm(query.searchQuery!, result, result.pagination.total, newNextToken, query);

      return result;
    } catch (error) {
      addErrorBanner(getString("errors.general"));
      return undefined;
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    isLoading,
    isLoadingMore,
    hasMoreItems,
    searchCatalogItems,
    searchNext,
    items,
    totalCount,
    nextToken,
    queryId: queryId.current,
    actions: {
      onNameSortChange,
      onPriceSortChange,
      onBestMatchSortChange,
      onRatingsSortChange,
      onFilterChange,
      transformToCatalogicalFilters,
    },
    currentSort,
    nameSort,
    priceSort,
    bestMatchSort,
    ratingsSort,
    filter,
    initializeFromUrl,
    shouldUseBestMatch,
  };
}
